import createCache from '@emotion/cache';
import {CacheProvider} from '@emotion/react';
import {Theme, ThemeProvider} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {RouterProvider} from '@tanstack/react-router';
import {withLDProvider} from 'launchdarkly-react-client-sdk';
import React, {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {Provider} from 'react-redux';
import {localeMap, tolgeeInstance} from 'tolgee';

// Initialize Sentry before anything else
import './sentry';

import {initJam} from './jam';
// Import external-svg-loader for dynamic SVG loading
import 'external-svg-loader';

import {theme} from '~constants/theme';
import {shouldMountLaunchDarkly} from '~utils/launchDarklyEnv';
import {setRouterInstance} from '~utils/routeUtil';
import {store} from '~redux/index';

import {router} from '~router/router';
import {launchDarklyClientID} from '~components/common/ThirdPartyScipts';
import {UserbackProvider} from '~components/common/UserbackProvider';
import {AbilitiesContextProvider} from 'src/contexts/AbilitiesContextProvider';
import {installE2eBridge} from './e2e/e2eBridge';
import {queryClient} from './queryClient';

import './styles.css';

const isE2eBuild = import.meta.env.VITE_E2E === 'true';

// Set the router instance for route utilities to use
setRouterInstance(router);

if (!isE2eBuild) {
  initJam();
}

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

// Create custom cache to disable CSS vendor prefixing during development
// see https://stackoverflow.com/a/65773154 and https://emotion.sh/docs/cache-provider
const emotionCache = createCache({
  key: 'maddoxai',
  ...(import.meta.env.NODE_ENV !== 'production' && {stylisPlugins: []}),
});

if (isE2eBuild) {
  installE2eBridge({store, queryClient, router});
}

const isDevelopmentEnvironment = import.meta.env.NODE_ENV !== 'production';
const isTestEnv = import.meta.env.NODE_ENV === 'test';

function App() {
  const currentLanguage = tolgeeInstance.getLanguage() || 'en';
  return (
    <StrictMode>
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <QueryClientProvider client={queryClient}>
            {isDevelopmentEnvironment && !isTestEnv && !isE2eBuild && (
              <ReactQueryDevtools initialIsOpen={false} buttonPosition='top-left' />
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[currentLanguage]}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <AbilitiesContextProvider>
                  <UserbackProvider>
                    <RouterProvider router={router} />
                  </UserbackProvider>
                </AbilitiesContextProvider>
              </ThemeProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </CacheProvider>
      </Provider>
    </StrictMode>
  );
}

// Wrap with LaunchDarkly in production (and locally when VITE_LD_ENABLE_IN_DEV is set)
const launchDarklyClientIDValue = launchDarklyClientID ?? '';

const AppWithProviders = shouldMountLaunchDarkly
  ? withLDProvider({
      clientSideID: launchDarklyClientIDValue,
    })(App)
  : App;

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = typeof document !== 'undefined' ? document.getElementById('root') : null;

if (rootElement && !rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(<AppWithProviders />);
}
